import { useId, useMemo, useState } from "react";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { useServiceAreaCheck } from "@/customer/service_area/useServiceAreaCheck";
import type { ServiceAreaCheckResult } from "@/customer/service_area/types";
import type { Cart, DeliveryAddress } from "@/customer/cart/types";

import { usePatchCart } from "@/customer/cart/hooks";

// Address section. Single-line "search" input runs the service-area
// check live; once serviceable, the rest of the address (city, state,
// postal_code) is collected with a quick form. Saving PATCHes the cart
// and advances the customer to the next step.
//
// We deliberately skip Mapbox autocomplete in this slice — the public
// MAPBOX_TOKEN isn't shipped to the customer SPA and the spec accepts
// a typed address with server-side service-area validation. Geocoder
// autocomplete is a follow-up.
export function DeliveryAddressSection({
  cart,
  onAdvance
}: {
  cart: Cart;
  onAdvance(): void;
}): JSX.Element {
  const saved = cart.payload.delivery_address;
  const [search, setSearch] = useState(saved?.line1 ? formatSearch(saved) : "");
  const { status, data, error, flush } = useServiceAreaCheck(search, 300);

  return (
    <div className="space-y-4">
      <p className="text-sm text-zinc-600">
        We deliver to metro Denver and metro Las Vegas. Enter your delivery
        address and we&rsquo;ll confirm before you continue.
      </p>

      <div className="space-y-2">
        <Label className="text-sm font-medium text-brand-black">Address</Label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            type="text"
            autoComplete="street-address"
            placeholder="123 Main St, Denver, CO"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                flush();
              }
            }}
            className="flex-1"
          />
          <Button type="button" variant="outline" onClick={flush}>
            Check availability
          </Button>
        </div>
        <CheckResult status={status} data={data} error={error} />
      </div>

      {data && data.serviceable ? (
        <SaveAddressForm
          existing={saved}
          formatted={data.formatted_address}
          coordinates={data.geocoded_point}
          onSaved={onAdvance}
        />
      ) : null}
    </div>
  );
}

function formatSearch(a: DeliveryAddress): string {
  const parts = [a.line1, a.city, a.state, a.postal_code].filter(Boolean);
  return parts.join(", ");
}

function CheckResult({
  status,
  data,
  error
}: {
  status: ReturnType<typeof useServiceAreaCheck>["status"];
  data: ServiceAreaCheckResult | undefined;
  error: ReturnType<typeof useServiceAreaCheck>["error"];
}): JSX.Element | null {
  if (status === "idle") return null;
  if (status === "loading") {
    return (
      <p role="status" className="text-sm text-zinc-600">
        Checking address…
      </p>
    );
  }
  if (status === "error") {
    return (
      <Alert variant="destructive">
        Couldn&rsquo;t check your address — please try again.
        {error ? <span className="ml-1 text-xs">({error.message})</span> : null}
      </Alert>
    );
  }
  if (!data) return null;
  if (data.serviceable) {
    const closest = data.eligible_yards[0];
    return (
      <p className="text-sm text-emerald-700">
        <span aria-hidden="true">✓ </span>
        <strong className="font-semibold">We deliver here.</strong>
        {closest ? (
          <>
            {" "}
            Drive distance: <strong>{Math.round(closest.drive_distance_miles)} mi</strong>.
          </>
        ) : null}
      </p>
    );
  }
  return (
    <Alert variant="destructive">
      <strong className="font-semibold">We don&rsquo;t deliver to this address.</strong>{" "}
      Our service area is metro Denver and metro Las Vegas. Call us at{" "}
      <a className="underline" href="tel:+13034354328">
        (303) 435-4328
      </a>{" "}
      if you think this is wrong.
    </Alert>
  );
}

interface SaveAddressFormProps {
  existing: DeliveryAddress | undefined;
  formatted: string;
  coordinates: { lat: number; lng: number };
  onSaved(): void;
}

function SaveAddressForm({
  existing,
  formatted,
  coordinates,
  onSaved
}: SaveAddressFormProps): JSX.Element {
  const parsed = useMemo(() => parseFormatted(formatted), [formatted]);
  const ids = {
    line1: useId(),
    line2: useId(),
    city: useId(),
    state: useId(),
    postal: useId()
  };
  const [line1, setLine1] = useState(existing?.line1 ?? parsed.line1);
  const [line2, setLine2] = useState(existing?.line2 ?? "");
  const [city, setCity] = useState(existing?.city ?? parsed.city);
  const [state, setState] = useState(existing?.state ?? parsed.state);
  const [postalCode, setPostalCode] = useState(existing?.postal_code ?? parsed.postal_code);
  const patch = usePatchCart();

  const canSave = line1 && city && state && postalCode;

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!canSave) return;
    const address: DeliveryAddress = {
      line1,
      city,
      state,
      postal_code: postalCode,
      country: "US",
      lat: coordinates.lat,
      lng: coordinates.lng
    };
    if (line2) address.line2 = line2;
    patch.mutate({ delivery_address: address }, { onSuccess: () => onSaved() });
  }

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <div className="md:col-span-2">
        <Label htmlFor={ids.line1} className="mb-1 block text-sm">
          Street address
        </Label>
        <Input id={ids.line1} value={line1} onChange={(e) => setLine1(e.target.value)} required />
      </div>
      <div className="md:col-span-2">
        <Label htmlFor={ids.line2} className="mb-1 block text-sm">
          Apt / unit (optional)
        </Label>
        <Input id={ids.line2} value={line2} onChange={(e) => setLine2(e.target.value)} />
      </div>
      <div>
        <Label htmlFor={ids.city} className="mb-1 block text-sm">
          City
        </Label>
        <Input id={ids.city} value={city} onChange={(e) => setCity(e.target.value)} required />
      </div>
      <div>
        <Label htmlFor={ids.state} className="mb-1 block text-sm">
          State
        </Label>
        <Input id={ids.state} value={state} onChange={(e) => setState(e.target.value)} required maxLength={2} />
      </div>
      <div>
        <Label htmlFor={ids.postal} className="mb-1 block text-sm">
          ZIP code
        </Label>
        <Input id={ids.postal} value={postalCode} onChange={(e) => setPostalCode(e.target.value)} required />
      </div>
      <div className="flex items-end justify-end md:col-span-2">
        <Button type="submit" disabled={!canSave || patch.isPending}>
          {patch.isPending ? "Saving…" : "Save and continue"}
        </Button>
      </div>
      {patch.isError ? (
        <Alert variant="destructive" className="md:col-span-2">
          We couldn&rsquo;t save that address — try again or call (303) 435-4328.
        </Alert>
      ) : null}
    </form>
  );
}

interface ParsedAddress {
  line1: string;
  city: string;
  state: string;
  postal_code: string;
}

// Mapbox returns "123 Main Street, Denver, Colorado 80202, United States".
// We split into address parts. Falls back gracefully if the format isn't
// canonical — the customer can still edit each field.
function parseFormatted(formatted: string): ParsedAddress {
  const parts = formatted.split(",").map((s) => s.trim()).filter(Boolean);
  const line1 = parts[0] ?? "";
  const city = parts[1] ?? "";
  const stateZip = parts[2] ?? "";
  const match = stateZip.match(/^(.+?)\s+(\d{5})(?:-\d{4})?$/);
  return {
    line1,
    city,
    state: match ? abbrState(match[1]) : "",
    postal_code: match ? match[2] : ""
  };
}

const STATE_ABBREVIATIONS: Record<string, string> = {
  colorado: "CO",
  nevada: "NV"
};

function abbrState(name: string): string {
  const lc = name.toLowerCase();
  if (STATE_ABBREVIATIONS[lc]) return STATE_ABBREVIATIONS[lc];
  if (name.length === 2) return name.toUpperCase();
  return name;
}
